import { Global } from '@emotion/react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ConfigProvider, RobinTheme } from '@robinpowered/ui-kit';
import { RKTheme } from '@robinpowered/design-system';
import { I18nextProvider } from 'react-i18next';
import {
  GlobalErrorBoundary,
  HomepageRedirect,
  LegacyEventRedirect,
} from 'components';
import { i18n } from 'i18n';
import { cssReset } from 'cssReset';
import { config } from 'config';
import {
  AmplitudeProvider,
  ApolloContextProvider,
  AuthContextProvider,
  useApolloContext,
  useAuthContext,
} from 'contexts';
import {
  useAccessToken,
  FeatureFlagProvider,
} from '@robinpowered/dashboard-apps-common';
import { LocalizationContext } from 'contexts/LocalizationContext';
import { AppShell } from 'components/AppShell';
import { isUnauthenticatedPath } from 'constants/unauthPaths';
import { PropsWithChildren, useEffect } from 'react';
import { isUsingParentToken } from 'utils/isUsingParentToken';

export const App = (): JSX.Element => {
  return (
    <I18nextProvider i18n={i18n}>
      <GlobalErrorBoundary>
        <Global styles={cssReset} />
        <RKTheme>
          <ConfigProvider theme={{ ...RobinTheme, cssVar: { key: 'shell' } }}>
            <BrowserRouter>
              <ApolloContextProvider>
                {/*
                  FeatureFlagProvider must sit ABOVE AuthContextProvider:
                  AuthContext (via useUserPermissions) consumes feature flags,
                  and a consumer mounted above its provider only ever receives
                  the default context value (checkFlag -> false). Its inputs
                  (tenantId from ApolloContext, accessToken from the session
                  cookie) are both available here, above AuthContext.
                */}
                <ShellFeatureFlagProvider>
                  <AuthContextProvider>
                    <LocalizationContext>
                      <RootRouteTree />
                    </LocalizationContext>
                  </AuthContextProvider>
                </ShellFeatureFlagProvider>
              </ApolloContextProvider>
            </BrowserRouter>
          </ConfigProvider>
        </RKTheme>
      </GlobalErrorBoundary>
    </I18nextProvider>
  );
};

// Provides feature flags above AuthContextProvider. Reads its inputs from
// ApolloContext (tenantId) and the session cookie (accessToken), both of which
// are in scope here without depending on AuthContext.
const ShellFeatureFlagProvider = ({
  children,
}: PropsWithChildren): JSX.Element => {
  const { tenantId } = useApolloContext();
  const { featureFlagUrl, sessionCookieName } = config;
  const { accessToken } = useAccessToken(sessionCookieName);

  return (
    <FeatureFlagProvider
      featureFlagUrl={featureFlagUrl}
      tenantId={tenantId || null}
      accessToken={accessToken}
    >
      {children}
    </FeatureFlagProvider>
  );
};

const RootRouteTree = (): JSX.Element => {
  const { dashboardUrl, env } = config;
  // accessToken comes from AuthContext (which derives it from the same session
  // cookie) rather than calling useAccessToken again here.
  const {
    loading: authLoading,
    isUnauthenticated,
    accessToken,
  } = useAuthContext();

  useEffect(() => {
    if (isUsingParentToken()) {
      return;
    }

    // For applications like deliveries that have unauthenticated paths, we don't want to redirect to the login page
    const pathname = window.location.pathname;
    if (isUnauthenticatedPath(pathname)) {
      return;
    }

    if (
      env !== 'development' &&
      (!accessToken || (!authLoading && isUnauthenticated))
    ) {
      const currentPath = pathname + window.location.search;
      const encodedPath = encodeURIComponent(currentPath);
      window.location.href = `${dashboardUrl}/login?redirectTo=${encodedPath}`;
    }
  }, [authLoading, isUnauthenticated, dashboardUrl, accessToken, env]);

  return (
    <AmplitudeProvider>
      <Routes>
        <Route path="/" element={<HomepageRedirect />} />
        {/* Bare event URLs inherited from the legacy AngularJS dashboard.
            Cloudflare routes /event(s) and /event-composer (excluding series
            URLs) to the shell bucket. LegacyEventRedirect resolves the org
            and forwards to the canonical ?event=... form under the
            meeting-rooms page.
            Both /event/:id (singular) and /events/:id (plural) are accepted:
            the legacy AngularJS state is main.events.event at /events/:id
            and most in-the-wild links use the plural form. The Cloudflare
            exclusion rule for series URLs covers both singular and plural
            event prefixes. See specs/legacy-event-redirect/spec.md. */}
        <Route
          path="event/:eventID"
          element={<LegacyEventRedirect mode="event" />}
        />
        <Route
          path="events/:eventID"
          element={<LegacyEventRedirect mode="event" />}
        />
        <Route
          path="event-composer/:eventID"
          element={<LegacyEventRedirect mode="event-composer-edit" />}
        />
        <Route
          path="event-composer"
          element={<LegacyEventRedirect mode="event-composer-new" />}
        />
        <Route path=":orgSlug" element={<HomepageRedirect />} />
        <Route path=":orgSlug/*" element={<AppShell />} />
      </Routes>
    </AmplitudeProvider>
  );
};
